import { db } from '../firebase/firebase';
import { 
    doc, 
    getDoc, 
    setDoc, 
    updateDoc,
    serverTimestamp
} from 'firebase/firestore';

class AdminConfigService {
    constructor() {
        this.collectionName = 'adminConfig';
        this.documentName = 'apiConfig';
        this.defaultAlphaQuarkUrl = 'https://ccxtprod.alphaquark.in';
    }

    // Get current API configuration
    async getApiConfig() {
        try {
            const docRef = doc(db, this.collectionName, this.documentName);
            const docSnap = await getDoc(docRef);
            
            if (docSnap.exists()) {
                return docSnap.data();
            } else {
                // Return default configuration if no config exists
                return {
                    alphaQuark: {
                        baseURL: this.defaultAlphaQuarkUrl,
                        isActive: true
                    },
                    dashboard: {
                        enableDashboard: false
                    }
                };
            }
        } catch (error) {
            console.error('Error fetching API config:', error);
            // Return default configuration on error
            return {
                alphaQuark: {
                    baseURL: this.defaultAlphaQuarkUrl,
                    isActive: true
                },
                dashboard: {
                    enableDashboard: false
                }
            };
        }
    }

    // Get AlphaQuark base URL
    async getAlphaQuarkUrl() {
        try {
            const config = await this.getApiConfig();
            return config.alphaQuark?.baseURL || this.defaultAlphaQuarkUrl;
        } catch (error) {
            console.error('Error getting AlphaQuark URL:', error);
            return this.defaultAlphaQuarkUrl;
        }
    }

    // Get dashboard configuration
    async getDashboardConfig() {
        try {
            const config = await this.getApiConfig();
            return config.dashboard || { enableDashboard: false };
        } catch (error) {
            console.error('Error getting dashboard config:', error);
            return { enableDashboard: false };
        }
    }

    // Check if dashboard is enabled
    async isDashboardEnabled() {
        try {
            const dashboardConfig = await this.getDashboardConfig();
            return dashboardConfig.enableDashboard || false;
        } catch (error) {
            console.error('Error checking dashboard status:', error);
            return false;
        }
    }

    // Update API configuration
    async updateApiConfig(apiConfig) {
        try {
            // Validate the configuration
            this.validateApiConfig(apiConfig);
            
            const docRef = doc(db, this.collectionName, this.documentName);
            const updateData = {
                ...apiConfig,
                updatedAt: serverTimestamp()
            };
            
            await setDoc(docRef, updateData);
            
            return { success: true, message: 'API configuration updated successfully' };
        } catch (error) {
            console.error('Error updating API config:', error);
            throw new Error(`Failed to update API configuration: ${error.message}`);
        }
    }

    // Update AlphaQuark URL specifically
    async updateAlphaQuarkUrl(baseURL) {
        try {
            // Validate URL
            if (!this.isValidUrl(baseURL)) {
                throw new Error('Invalid URL format. Please provide a valid HTTPS URL.');
            }
            
            const currentConfig = await this.getApiConfig();
            const updatedConfig = {
                ...currentConfig,
                alphaQuark: {
                    ...currentConfig.alphaQuark,
                    baseURL: baseURL
                }
            };
            
            return await this.updateApiConfig(updatedConfig);
        } catch (error) {
            console.error('Error updating AlphaQuark URL:', error);
            throw error;
        }
    }

    // Test API URL
    async testApiUrl(baseURL, endpoint = '/health') {
        const testUrl = `${baseURL}${endpoint}`;
        
        try {
            console.log(`Testing API URL: ${testUrl}`);
            
            const response = await fetch(testUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                },
                signal: AbortSignal.timeout(10000) // 10 seconds timeout
            });
            
            return {
                success: response.ok,
                status: response.status,
                statusText: response.statusText,
                url: testUrl,
                timestamp: new Date().toISOString()
            };
        } catch (error) {
            return {
                success: false,
                error: error.message,
                url: testUrl,
                timestamp: new Date().toISOString()
            };
        }
    }

    // Test AlphaQuark API with authentication
    async testAlphaQuarkApi(baseURL) {
        try {
            // Import the authenticated API service
            const { getAuthenticatedApiService } = await import('./AuthenticatedApiService');
            const apiService = getAuthenticatedApiService(baseURL);
            
            const testUrl = `${baseURL}/misc/rightspade/get-user-info/recommendations`;
            console.log(`Testing AlphaQuark API URL: ${testUrl}`);
            
            const response = await apiService.get('/misc/rightspade/get-user-info/recommendations');
            
            return {
                success: true,
                status: 200,
                statusText: 'OK',
                url: testUrl,
                timestamp: new Date().toISOString(),
                data: response
            };
        } catch (error) {
            const testUrl = `${baseURL}/misc/rightspade/get-user-info/recommendations`;
            return {
                success: false,
                error: error.message,
                url: testUrl,
                timestamp: new Date().toISOString()
            };
        }
    }

    // Validate API configuration
    validateApiConfig(apiConfig) {
        if (!apiConfig || typeof apiConfig !== 'object') {
            throw new Error('Invalid configuration format');
        }
        
        if (!apiConfig.alphaQuark) {
            throw new Error('AlphaQuark configuration is required');
        }
        
        if (!apiConfig.alphaQuark.baseURL) {
            throw new Error('AlphaQuark base URL is required');
        }
        
        if (!this.isValidUrl(apiConfig.alphaQuark.baseURL)) {
            throw new Error('Invalid AlphaQuark URL format');
        }
    }

    // Validate URL
    isValidUrl(string) {
        try {
            const url = new URL(string);
            return url.protocol === 'https:' || url.protocol === 'http:';
        } catch (_) {
            return false;
        }
    }

    // Initialize default configuration
    async initializeDefaultConfig() {
        try {
            const defaultConfig = {
                alphaQuark: {
                    baseURL: this.defaultAlphaQuarkUrl,
                    isActive: true
                },
                dashboard: {
                    enableDashboard: false
                }
            };
            
            await this.updateApiConfig(defaultConfig);
            return { success: true, message: 'Default configuration initialized' };
        } catch (error) {
            console.error('Error initializing default config:', error);
            throw error;
        }
    }
}

// Create singleton instance
let adminConfigServiceInstance = null;

export const getAdminConfigService = () => {
    if (!adminConfigServiceInstance) {
        adminConfigServiceInstance = new AdminConfigService();
    }
    return adminConfigServiceInstance;
};

export default AdminConfigService; 